import { useAuth } from './auth/useAuth';
import RenderError from './error/renderError';
import { useStoreGuid } from './lib/hooks';
import { useAppSelector } from './redux.ts';
import storeService from './store/store.service';
import { NotImplemented } from '@/components/notImplemented';
import type { TranslationKeys } from '@/translations/commonTranslatorConfig';
import { useTranslator } from '@/translations/translator';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faAnalytics,
    faBarcode,
    faBarcodeAlt,
    faBarcodeScan,
    faBoxes,
    faBug,
    faBullhorn,
    faCalendarAlt,
    faCartCirclePlus,
    faCashRegister,
    faCertificate,
    faClipboardList,
    faCodeMerge,
    faCog,
    faDatabase,
    faEye,
    faEyedropper,
    faFileImport,
    faGear,
    faHammer,
    faHistory,
    faInfo,
    faLocation,
    faMagnifyingGlass,
    faMap,
    faMapMarkerTimes,
    faMeterBolt,
    faNotebook,
    faObjectGroup,
    faPersonDolly,
    faQrcode,
    faRobot,
    faRocket,
    faScannerGun,
    faShieldCheck,
    faShoppingCart,
    faSquarePollVertical,
    faShop,
    faUsers,
    faWarehouseFull,
    faBook,
} from '@fortawesome/pro-regular-svg-icons';
import { Box } from '@mui/material';
import type { MakeOptional } from '@mui/x-date-pickers/internals';
import { filterTruthy } from '@whiz-cart/node-shared/helpers/filterTruthy';
import { BrandIcons } from '@whiz-cart/node-shared/models/brand/brand';
import { StoreGeneralConfig } from '@whiz-cart/node-shared/models/config/storeGeneralConfig';
import { ScopeParameters } from '@whiz-cart/ui-shared/auth/authTypes';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { ComponentType, LazyExoticComponent, lazy, useCallback, useMemo } from 'react';
import type { match } from 'react-router';

type ClaimParams = string | [string, ...ScopeParameters];

export interface Route {
    exact?: boolean;
    path: string;
    link: string;
    externalLink?: string;
    redirect?: string;
    icon?: IconDefinition;
    brandIcon?: keyof BrandIcons;
    translationKey?: TranslationKeys;
    label?: string;
    englishLabel?: string;
    group?: string;
    scope?: 'global' | 'store' | 'storeDraft';
    claims?: ClaimParams[] | { global?: ClaimParams[]; store?: ClaimParams[] };
    roles?: string[];
    component?: LazyExoticComponent<any>;
    children?: Route[];
    nestedChildren?: Route[];
    showOnlyWithChildren?: boolean;
    parentInNavigation?: Route;
    nested?: boolean | 'fullpage';
    show?: (storeConfig: StoreGeneralConfig) => boolean;
}

type RawRoute =
    | (Omit<MakeOptional<Route, 'path' | 'link'>, 'component' | 'children' | 'isNested'> & {
          link?: string;
          component?: () => Promise<{ default: ComponentType | ComponentType<{ match: match }> }>;
          children?: (RawRoute | undefined | null | false | '' | 0)[];
      })
    | false
    | undefined
    | null;

async function mapImport<T, K extends keyof T>(importPromise: Promise<T>, map: K): Promise<{ default: T[K] }>;
async function mapImport<T, S>(importPromise: Promise<{ default: T }>, map: (def: T) => S): Promise<{ default: S }>;
async function mapImport<T>(importPromise: Promise<{ default: T }>, map: string | ((def: T) => any)) {
    const comp = await importPromise;
    return typeof map === 'string' ? { ...comp, default: (comp as any)[map] } : { ...comp, default: map(comp.default) };
}

function withProps<P, T extends ComponentType<P> = ComponentType<P>>(importPromise: Promise<{ default: T }>, defaultProps: P) {
    return mapImport(importPromise, (Component: any) => (props: any) => <Component {...defaultProps} {...props} />);
}

function transformRoute(rawRoute: RawRoute, parent?: Route): Route[] {
    if (!rawRoute) {
        return [];
    }

    const route: Route = {
        ...rawRoute,
        path: [parent?.path, rawRoute.path].filter(Boolean).join('/'),
        link: urlService.resolve(parent?.link ?? '/', rawRoute.link ?? rawRoute.path ?? ''),
        redirect: rawRoute.redirect ? urlService.resolve(parent?.link ?? '/', rawRoute.path ?? '', rawRoute.redirect) : undefined,
        scope: rawRoute.scope ?? parent?.scope,
        claims: rawRoute.claims ?? parent?.claims,
        roles: rawRoute.roles ?? parent?.roles,
        component: rawRoute.component && lazy(() => loadComponent(rawRoute.component)),
        parentInNavigation: parent && (!!rawRoute.label || !!rawRoute.translationKey) ? parent : undefined,
        children: undefined,
        showOnlyWithChildren: rawRoute.showOnlyWithChildren ?? false,
    };

    const children = rawRoute.children?.filter(filterTruthy).flatMap((child) => transformRoute(child, route));
    const flatChildren = children?.filter((child) => !child.nested) ?? [];
    route.children = children?.filter((child) => child.parentInNavigation === route);
    route.nestedChildren = children?.filter((child) => child.nested);

    return flatChildren.concat(route);
}

function flattenRoutes(...routes: RawRoute[]): Route[] {
    const flatten = (route: Route): Route[] => [...(route.children?.flatMap(flatten) ?? []), route];
    return routes.flatMap((route) => transformRoute(route)).flatMap(flatten);
}

const routes = [
    ...flattenRoutes(
        // Kunden
        {
            path: '/sm',
            icon: faEye,
            translationKey: 'navigation.sessionMonitor',
            group: 'navigation.customers',
            scope: 'store',
            claims: ['session/read'],
            component: () => import('./sessionMonitor/sessionMonitor.component'),
        },
        {
            path: '/checkout',
            component: () => import('./checkout/checkout.component'),
            icon: faCashRegister,
            translationKey: 'navigation.checkout',
            group: 'navigation.customers',
            scope: 'store',
            // payment/read alone also covers read-only roles (detective, auditor), which only get the session history
            claims: ['checkout/read'],
            children: [
                {
                    path: 'cartLogin',
                    component: () =>
                        withProps<any>(import('./cartLogin/cartLogin.component'), {
                            back: '/checkout',
                        }),
                    // Also opened from the session history's login code action, so it follows the login code claim
                    claims: ['ui/cartLoginCode'],
                },
                {
                    path: 'payment/:sessionGuid/:paymentGuid/:step?/:subStep?',
                    component: () => import('./checkout/payment.component'),
                },
            ],
        },
        {
            path: '/integrityCheck',
            scope: 'store',
            component: () => import('./integrityCheck/integrityCheck.component'),
            children: [
                {
                    path: 'details/:paymentRequestGuid/',
                    scope: 'store',
                    component: () => import('./integrityCheck/integrityCheckDetailsWrapper.component'),
                },
            ],
            claims: ['payment/read', 'payment/write'],
        },
        {
            path: '/session/:sessionGuid?/:paymentGuid?',
            link: '/session',
            component: () => import('./checkout/checkoutHistory.component'),
            icon: faHistory,
            translationKey: 'navigation.sessionHistory',
            group: 'navigation.customers',
            scope: 'store',
            claims: ['payment/read'],
        },
        {
            path: '/cartLogin/',
            group: 'navigation.customers',
            component: () => import('./cartLogin/cartLogin.component'),
            icon: faQrcode,
            translationKey: 'navigation.loginCode',
            scope: 'store',
            claims: ['ui/cartLoginCode'],
        },
        {
            path: '/dsi/inspect/suspicious',
            component: () => import('./dsi/inspector/inspect/dsiInspect.component'),
            claims: ['dsi/read', 'dsi/write'],
        },
        {
            path: '/dsi/inspect/confirm/:sessionGuid/:step?',
            component: () => import('./dsi/inspector/confirm/dsiConfirm.component'),
            claims: ['dsi/read', 'dsi/write'],
        },
        {
            path: '/dsi',
            link: '/dsi',
            group: 'navigation.customers',
            icon: faShieldCheck,
            translationKey: 'navigation.dsi',
            claims: ['dsi/read', 'dsi/write'],
            children: [
                {
                    path: 'live',
                    translationKey: 'navigation.dsiLiveCheck',
                    claims: ['session/imageDetection/read', 'session/imageDetection/confirm'],
                    children: [
                        {
                            path: ':sessionGuid',
                            component: () => import('./dsi/live/session/dsiLiveSessionView.component'),
                        },
                        {
                            path: '',
                            component: () => import('./dsi/live/dsiLiveView.component'),
                            icon: faMagnifyingGlass,
                        },
                    ],
                },
                {
                    path: 'inconsistent',
                    translationKey: 'navigation.dsiInconsistentData',
                    claims: ['dsi/read', 'dsi/write'],
                    children: [
                        {
                            path: ':modelId/:sessionGuid?/:eventId?',
                            component: () => import('./dsi/inconsistentLabelling/inconsistentLabellingView'),
                        },
                        {
                            path: '',
                            component: () => import('./dsi/inconsistentLabelling/inconsistentLabellingOverview'),
                        },
                    ],
                },
                {
                    path: ':sessionGuid?/:step?',
                    component: () => import('./dsi/overview/dsi.component'),
                },
            ],
        },
        // Artikel
        {
            path: '/productBrowser',
            icon: faBoxes,
            translationKey: 'navigation.productBrowser',
            group: 'navigation.products',
            scope: 'store',
            component: () => import('./productBrowser/productBrowserList.component'),
            claims: ['product/read'],
            children: [
                {
                    path: ':itemId',
                    component: () => import('./productBrowser/productBrowserEdit.component'),
                    nested: true,
                },
            ],
        },
        {
            path: '/cg/:fullId?',
            link: '/cg',
            icon: faObjectGroup,
            translationKey: 'navigation.commodityGroups',
            group: 'navigation.products',
            component: () => import('./commodityGroup/cgView.tsx'),
            claims: ['ui/commodityGroupEditor'],
        },
        {
            path: '/product/barcodeGenerator',
            link: '/product/barcodeGenerator',
            group: 'navigation.products',
            component: () => import('./barcodeGenerator/barcodeGenerator.component'),
            icon: faBarcodeAlt,
            translationKey: 'navigation.barcodeGenerator',
            claims: ['ui/barcodeGenerator'],
        },
        {
            path: '/product/barcodeScanner',
            link: '/product/barcodeScanner',
            group: 'navigation.products',
            component: () => import('./barcodeScanner/barcodeScanner.component'),
            icon: faScannerGun,
            translationKey: 'product.barcodeScanner',
            claims: ['ui/barcodeScanner'],
        },
        {
            path: '/product/import',
            link: '/product/import',
            group: 'navigation.products',
            component: () => import('./productImport/productImport.component'),
            icon: faFileImport,
            translationKey: 'product.import',
            roles: ['admin'],
        },
        {
            path: '/product/details',
            group: 'navigation.products',
            component: () => import('./product/productDetails.component'),
            icon: faInfo,
            translationKey: 'navigation.details',
            scope: 'store',
            claims: ['ui/productDetails'],
            children: [
                {
                    path: 'mhdgroup',
                    component: () => import('./product/productDetails.component'),
                    nested: true,
                },
                {
                    path: ':barcode(\\d+)?/:mhdgroup',
                    component: () => import('./product/productDetails.component'),
                    nested: true,
                },
            ],
        },
        {
            path: '/product/mhd',
            link: '/product/mhd',
            group: 'navigation.products',
            component: () => import('./mhd/mhd.component'),
            icon: faCalendarAlt,
            translationKey: 'navigation.mhd',
            scope: 'store',
            claims: ['product/expiration/read'],
        },
        {
            path: '/product/nobarcode',
            scope: 'store',
            icon: faBarcodeScan,
            translationKey: 'navigation.noBarcode',
            group: 'navigation.products',
            claims: ['product/noBarcode/write'],
            component: () => import('./noBarcode/noBarcode.component'),
        },
        {
            path: '/pluHierarchy',
            scope: 'store',
            brandIcon: 'pluHierarchy',
            group: 'navigation.products',
            translationKey: 'navigation.pluHierarchy',
            component: () => import('./pluHierarchy/pluHierarchyEditor'),
            claims: ['plu/hierarchy/read'],
            show: (config) => config?.enablePluHierarchy === true,
        },
        {
            path: '/product/notfoundproducts/',
            scope: 'store',
            icon: faMapMarkerTimes,
            translationKey: 'navigation.NotFoundProducts',
            group: 'navigation.products',
            roles: ['itOperations', 'pentland'],
            component: () => import('./product/productsNotFoundNavigation.component'),
        },
        // Technik
        {
            path: '/infrastructure/config',
            group: 'navigation.infrastructure',
            component: () => import('./systemConfig/systemConfig.component'),
            icon: faCog,
            translationKey: 'navigation.settings',
            roles: ['pentland', 'itOperations'],
        },
        {
            path: '/infrastructure/devOps',
            group: 'navigation.infrastructure',
            component: () => import('./devOps/devOps.component'),
            icon: faCog,
            translationKey: 'navigation.devops',
            roles: ['pentland', 'itOperations'],
            children: [
                {
                    path: 'buildMonitor',
                    component: () => import('./buildMonitor/buildMonitor'),
                    icon: faHammer,
                    translationKey: 'navigation.buildMonitor',
                    roles: ['pentland'],
                },

                {
                    path: 'releases',
                    component: () => import('./devOps/ado/adoRepos'),
                    icon: faCodeMerge,
                    translationKey: 'navigation.releases',
                    roles: ['pentland'],
                },

                config?.feature?.logAnalyzer && {
                    path: 'logs',
                    component: () => import('./logAnalyzer/logAnalyzer'),
                    icon: faHammer,
                    translationKey: 'navigation.logAnalyzer',
                    roles: ['pentland'],
                },
                config?.feature?.logAnalyzer && {
                    path: 'storeLogs',
                    component: () => import('./logAnalyzer/storeLogAnalyzer'),
                    icon: faHammer,
                    translationKey: 'navigation.storeLogAnalyzer',
                    roles: ['pentland'],
                },

                {
                    path: 'dataSync',
                    icon: faShoppingCart,
                    label: 'DataSync',
                    component: () => import('./devOps/dataSync/dataSync'),
                },

                {
                    path: 'cartLocation',
                    icon: faLocation,
                    label: 'GPS',
                    component: () => import('./devOps/cartLocations/cartLocations'),
                    roles: ['pentland'],
                },

                {
                    path: 'storybook',
                    icon: faBook,
                    label: 'Storybook',
                    component: () => import('./devOps/storybook/storybook.component'),
                    roles: ['pentland'],
                },

                config?.indexManagerUrl && {
                    path: 'indexManager',
                    icon: faMagnifyingGlass,
                    label: 'Index Manager',
                    externalLink: config.indexManagerUrl,
                    roles: ['pentland'],
                },
            ],
        },

        {
            path: '/infrastructure/cart',
            component: () => import('./carts/cartOverview.component'),
            group: 'navigation.infrastructure',
            icon: faShoppingCart,
            translationKey: 'navigation.carts',
            claims: { global: ['cart/global/read'], store: ['cart/read'] },
            children: [
                {
                    path: 'global',
                    component: () => import('./carts/cartGlobal'),

                    children: [
                        {
                            path: 'guid/:cartGuid',
                            component: () =>
                                withProps(import('./carts/cartGlobalDetails'), {
                                    context: 'cart',
                                }),
                        },
                        {
                            path: 'fn/:framenumber',
                            component: () =>
                                withProps(import('./carts/cartGlobalDetails'), {
                                    context: 'cart',
                                }),
                        },
                    ],
                },
                {
                    path: 'store',
                    component: () => import('./carts/cartOverviewList.component'),
                    children: [
                        {
                            path: ':tab/details/:cartId',
                            component: () =>
                                withProps(import('./carts/cart.component'), {
                                    context: 'carts',
                                }),
                            nested: true,
                        },
                    ],
                },
            ],
        },

        {
            path: '/erp',
            redirect: 'plan',
            translationKey: 'erp.title',
            icon: faWarehouseFull,
            group: 'navigation.infrastructure',
            showOnlyWithChildren: true,
            claims: ['resourcePlanning/read'],
            children: [
                {
                    path: 'plan',
                    translationKey: 'erp.plan.navigation',
                    component: () => import('./resourcePlanner/planner'),
                    claims: ['resourcePlanning/read'],
                },
                {
                    path: 'assets',
                    translationKey: 'assets.title',
                    scope: 'storeDraft',
                    component: () => import('./resourcePlanner/assets'),
                    claims: ['assets/read'],
                    children: [
                        {
                            path: 'details/:assetId',
                            component: () => import('./resourcePlanner/assets/components/assetSimpleDetails.component'),
                            claims: ['chargingOption/read'],
                            nested: false,
                        },
                        {
                            path: ':locationType/:locationId/:assetId/move',
                            component: () => import('./resourcePlanner/assets/assetMoveView'),
                            nested: true,
                        },
                        {
                            path: ':locationType/:locationId/:assetId',
                            component: () => import('./resourcePlanner/assets/assetDetailsView'),
                            nested: true,
                        },
                    ],
                },
                {
                    path: 'props',
                    translationKey: 'erp.props.title',
                    component: () => import('./resourcePlanner/properties'),
                    claims: ['resourceProperties/read'],
                    children: [
                        {
                            path: 'new',
                            component: () => import('./resourcePlanner/properties/propertiesEdit'),
                            nested: true,
                        },
                        {
                            path: ':id',
                            component: () => import('./resourcePlanner/properties/propertiesEdit'),
                            nested: true,
                        },
                    ],
                },
                {
                    path: 'locations',
                    translationKey: 'erp.locations.title',
                    component: () => import('./resourcePlanner/locations'),
                    claims: ['assetLocations/read'],
                    children: [
                        {
                            path: 'types',
                            component: () => import('./resourcePlanner/locationTypes'),
                            claims: ['assetLocations/types/read'],
                            children: [
                                {
                                    path: 'new',
                                    component: () => import('./resourcePlanner/locationTypes/locationTypeEdit'),
                                    claims: ['assetLocations/types/write'],
                                    nested: true,
                                },
                                {
                                    path: ':id',
                                    component: () => import('./resourcePlanner/locationTypes/locationTypeEdit'),
                                    claims: ['assetLocations/types/write'],
                                    nested: true,
                                },
                            ],
                        },
                        {
                            path: 'new',
                            component: () => import('./resourcePlanner/locations/locationEdit'),
                            claims: ['assetLocations/write'],
                            nested: true,
                        },
                        {
                            path: ':id',
                            component: () => import('./resourcePlanner/locations/locationEdit'),
                            claims: ['assetLocations/write'],
                            nested: true,
                        },
                    ],
                },
                {
                    path: 'codes',
                    translationKey: 'erp.codes.navTitle',
                    component: () => import('./resourcePlanner/codes'),
                    claims: ['barcodeClassifications/read'],
                    children: [
                        {
                            path: 'new/:template?',
                            component: () => import('./resourcePlanner/codes/codesEdit'),
                            claims: ['barcodeClassifications/write'],
                            nested: true,
                        },
                        {
                            path: ':id',
                            component: () => import('./resourcePlanner/codes/codesEdit'),
                            claims: ['barcodeClassifications/write'],
                            nested: true,
                        },
                    ],
                },
            ],
        },

        {
            path: '/chargers',
            translationKey: 'assets.chargingOptions.title',
            component: () => import('./resourcePlanner/chargingOptions'),
            scope: 'storeDraft',
            icon: faMeterBolt,
            claims: ['chargingOption/read'],
            group: 'navigation.infrastructure',
            children: [
                {
                    path: 'new',
                    component: () => import('./resourcePlanner/chargingOptions/form/chargingOptionsEdit.tsx'),
                    claims: ['chargingOption/write'],
                },
                {
                    path: ':id',
                    component: () => import('./resourcePlanner/chargingOptions/form/chargingOptionsEdit.tsx'),
                    claims: ['chargingOption/write'],
                },
            ],
        },

        {
            path: '/infrastructure/setup',
            group: 'navigation.infrastructure',
            icon: faCartCirclePlus,
            translationKey: 'navigation.setup',
            scope: 'store',
            claims: ['cartSetup/initSetup/write'],
            redirect: 'active',
            children: [
                {
                    path: 'active',
                    translationKey: 'navigation.cartsInSetup',
                    component: () => import('./cartsInSetup/cartsInSetup.component'),
                    children: [
                        {
                            path: 'create',
                            component: () => import('./deployment/cartCreate.component'),
                        },

                        {
                            path: 'technicalCodes',
                            component: () => import('./deployment/cartTechnicalCodes.component'),
                            children: [
                                {
                                    path: 'setupWifi',
                                    component: () =>
                                        withProps(import('./deployment/cartTechnicalCodes.component'), { viewMode: 'setupWifi' } as const),
                                },
                            ],
                        },
                    ],
                },

                {
                    path: 'log/:mac',
                    component: () => import('./cartsInSetup/cartsInSetupLog'),
                },

                {
                    path: 'replacements',
                    translationKey: 'navigation.cartReplacements',
                    component: () => import('./cartReplacements/cartReplacements.component'),
                },

                {
                    path: 'auto',
                    translationKey: 'autoCartSetup.title',
                    component: () => import('./cartsInSetup/autoCartSetup/autoCartSetup'),
                },
            ],
        },

        {
            path: '/infrastructure/deployment',
            redirect: 'list',
            group: 'navigation.infrastructure',
            icon: faRocket,
            translationKey: 'navigation.deployments',
            roles: ['devops', 'rollout', 'pentland', 'itOperations'],
            children: [
                {
                    path: 'list',
                    component: () => import('./deployment/deploymentOverview.component'),
                },

                {
                    path: 'cart/:cartId',
                    component: () =>
                        withProps(import('./carts/cart.component'), {
                            context: 'deployment',
                        }),
                },

                {
                    path: 'package',
                    redirect: 'list',
                    translationKey: 'deployment.list.title',
                    roles: ['devops', 'itOperations'],
                    children: [
                        {
                            path: 'list/:query?',
                            component: () => import('./deployment/deploymentPackageList.component'),
                        },
                        {
                            path: 'create/:basePackageGuid?',
                            component: () => mapImport(import('./deployment/deploymentPackageCreate.component'), 'DeploymentPackageCreate'),
                        },
                        {
                            path: ':packageGuid',
                            component: () => import('./deployment/deploymentPackageDetails.component'),
                        },
                    ],
                },

                {
                    path: 'promoted',
                    translationKey: 'deployment.promoted.list.title',
                    roles: ['devops', 'itOperations'],
                    children: [
                        {
                            path: 'create/:promotedVersionGuid?',

                            component: () => import('./deployment/promotedVersionCreate'),
                        },
                        {
                            path: ':name/:version',
                            component: () => import('./deployment/promotedVersionDetails'),
                        },
                        {
                            path: '',
                            component: () => import('./deployment/promotedVersionList.component'),
                        },
                    ],
                },

                {
                    path: 'groups',
                    translationKey: 'deployment.groups.title',
                    roles: ['devops', 'itOperations'],
                    component: () => import('./deployment/deploymentGroups.component'),
                },
                {
                    path: 'activities',
                    translationKey: 'deployment.activities.title',
                    roles: ['devops', 'itOperations'],
                    component: () => import('./deployment/activities/deploymentActivities.component'),
                },
                {
                    path: 'history',
                    translationKey: 'deployment.history.title',
                    roles: ['devops', 'itOperations'],
                    component: () => import('./deployment/history/history.component'),
                },
                {
                    path: 'errors',
                    translationKey: 'deployment.errorsRoute.title',
                    roles: ['devops', 'itOperations'],
                    component: () => import('./deployment/errors/deploymentErrors.component'),
                },
            ],
        },
        {
            path: '/infrastructure/defects',
            group: 'navigation.infrastructure',
            component: () => import('./defects/defectOverview'),
            icon: faBug,
            translationKey: 'navigation.defects',
            claims: ['cart/defect/deactivate/read'],
        },

        //Verwaltung
        {
            path: '/user',
            icon: faUsers,
            translationKey: 'navigation.users',
            group: 'navigation.administration',
            roles: ['storeManager', 'rollout', 'foodAcademy', 'demo', 'pentland', 'itOperations', 'marketingManager'],
            component: () => import('./user/userList.component'),
            children: [
                {
                    path: 'create',
                    component: () => import('./user/userCreate.component'),
                },
                {
                    path: 'edit/:user',
                    component: () => import('./user/userEdit.component'),
                    nested: true,
                },
            ],
        },
        {
            path: '/customer',
            icon: faPersonDolly,
            translationKey: 'navigation.customers',
            group: 'navigation.administration',
            claims: [['customer/adminAccess', 'any']],
            component: () => import('./customer/customer.component'),
        },
        {
            path: '/infomaterial',
            component: () => import('./infoMaterial/infoMaterialView'),
            icon: faInfo,
            translationKey: 'navigation.info',
            group: 'navigation.administration',
            claims: [
                ['ui/information', 'any'],
                ['infomaterial/read', 'any'],
            ],
            children: [
                { path: 'new', component: () => import('./infoMaterial/infoMaterialEdit'), claims: [['infomaterial/write', 'any']] },
                { path: ':id', component: () => import('./infoMaterial/infoMaterialEdit'), claims: [['infomaterial/write', 'any']] },
            ],
        },
        {
            path: '/stores',
            icon: faShop,
            translationKey: 'navigation.stores',
            group: 'navigation.administration',
            redirect: 'list',
            claims: [['storeCreation/list', 'any']],
            children: [
                { path: 'list', component: () => import('./store/overview/storeList'), claims: [['storeCreation/list', 'any']] },
                {
                    path: 'create',
                    component: () => import('./store/details/storeCreate'),
                    claims: ['storeCreation/create'],
                },
                {
                    path: 'import',
                    component: () => import('./store/import/storeImport'),
                    claims: ['storeCreation/create'],
                },
                {
                    path: 'details/:milestoneGroup',
                    component: () => import('./store/details/storeMilestoneGroup'),
                    scope: 'storeDraft',
                    claims: ['storeCreation/get'],
                },
                {
                    path: 'details',
                    component: () => import('./store/details/storeDetails'),
                    scope: 'storeDraft',
                    claims: ['storeCreation/get'],
                },
                {
                    path: 'table',
                    component: () => import('./store/overview/storeListTable'),
                    claims: ['storeCreation/list'],
                },
            ],
        },
        {
            path: '/marketingPlatform',
            icon: faBullhorn,
            translationKey: 'navigation.marketing',
            group: 'navigation.administration',
            claims: [['advertisements/read', 'any']],
            component: () => import('./marketingPlatform/advertisements/advertisementList'),
            children: [
                {
                    path: 'create',
                    component: () => import('./marketingPlatform/advertisements/advertisementEdit'),
                    nested: 'fullpage',
                },
                {
                    path: 'edit/:advertisementGuid',
                    component: () => import('./marketingPlatform/advertisements/advertisementEdit'),
                    nested: 'fullpage',
                },
                {
                    path: 'reports',
                    component: () => import('./marketingPlatform/advertisements/marketingReports'),
                },
            ],
        },
        {
            path: '/product/promotions',
            link: '/product/promotions',
            group: 'navigation.administration',
            icon: faCertificate,
            translationKey: 'navigation.promotions',
            component: () => import('./promotions/promotions'),
            scope: 'store',
            // The page always loads the KDDR coupon actions, so it needs couponActions/read as well
            claims: ['promotion/read', 'digitalCoupons/read', 'couponActions/read'],
            children: [
                {
                    claims: ['promotion/read'],
                    path: 'voucherPromotion/:promotionGuid',
                    component: () =>
                        withProps(import('./promotions/form/discountsFormContainer.component'), {
                            context: 'voucherPromotion',
                        }),
                    nested: true,
                },
                {
                    claims: ['promotion/read'],
                    path: 'kddr/:promotionGuid',
                    component: () =>
                        withProps(import('./promotions/form/discountsFormContainer.component'), {
                            context: 'kddr',
                        }),
                    nested: true,
                },
                {
                    claims: ['digitalCoupons/read'],
                    path: 'easyDeal/:easyDealId',
                    component: () => import('./promotions/form/easyDealDetail'),
                    nested: true,
                },
                {
                    claims: ['recurringCustomerLoyalty/read'],
                    path: 'loyaltyRewards/:rewardId',
                    component: () => import('./promotions/form/loyaltyRewardsDetail'),
                    nested: true,
                },
            ],
        },
        {
            path: '/surveys',
            icon: faSquarePollVertical,
            translationKey: 'navigation.surveys',
            group: 'navigation.administration',
            claims: ['surveys/read'],
            component: () => import('./surveys/surveyOverview'),
            children: [
                {
                    path: 'results/:surveyId',
                    component: () => import('./surveys/survey'),
                    nested: false,
                },
                {
                    path: 'edit/:surveyId',
                    component: () => import('./surveys/surveyEdit'),
                    nested: false,
                },
                {
                    path: 'new',
                    component: () => import('./surveys/surveyEdit'),
                    nested: false,
                },
            ],
        },
        {
            path: '/masterData',
            component: () => mapImport(import('./masterData/masterDataOverview'), 'MasterDataOverview'),
            icon: faDatabase,
            translationKey: 'navigation.masterData',
            group: 'navigation.administration',
            // storeMasterData/read is also needed by other pages to resolve tenants and branches, so it does not grant the page
            claims: ['storeMasterData/view'],
        },
        {
            path: '/release',
            redirect: 'list',
            icon: faClipboardList,
            translationKey: 'navigation.releaseNotes',
            group: 'navigation.administration',
            claims: [['ui/releaseNotes', 'any']],
            children: [
                {
                    path: 'list/:query?',
                    component: () => mapImport(import('./releaseNotes/releaseNotesList'), 'ReleaseNotesList'),
                },
                {
                    path: 'display/:releaseNotesGuid',
                    component: () => mapImport(import('./releaseNotes/releaseNotesView'), 'ReleaseNotesView'),
                },
                {
                    path: 'edit/:releaseNotesGuid?',
                    component: () => mapImport(import('./releaseNotes/releaseNotesEdit'), 'ReleaseNotesEdit'),
                },
            ],
        },
        {
            path: '/storeSettings/:settingsKey?',
            link: '/storeSettings',
            component: () => import('./storeSettings/storeSettingsOverview.component'),
            icon: faGear,
            translationKey: 'navigation.storeSettings',
            group: 'navigation.administration',
            claims: [['storeSettings/read', 'any']],
        },
        {
            path: '/imageClassificationModels',
            link: '/imageClassificationModels',
            component: () => import('./imageClassification/imageClassifiers.tsx'),
            icon: faRobot,
            group: 'navigation.administration',
            translationKey: 'navigation.imageClassifier',
            claims: ['imageClassifierModels/read'],
        },
        config?.feature?.reporting && {
            path: '/reports/:activeId?',
            link: '/reports',
            component: () => mapImport(import('./reports/reportOverview'), 'ReportsOverview'),
            icon: faAnalytics,
            translationKey: 'navigation.reports',
            group: 'navigation.administration',
            scope: 'store',
            claims: ['reports/token/read'],
        },
        {
            path: '/mapEditor/:systemId?/:view?',
            link: '/mapEditor',
            component: () => mapImport(import('./mapEditor/mapEditorView'), 'MapEditorView'),
            icon: faMap,
            translationKey: 'navigation.mapEditor',
            group: 'navigation.administration',
            scope: 'storeDraft',
            claims: ['map/editor/writeBasic'],
            children: [
                {
                    path: 'shelfMapping',
                    component: () => import('./shelves/shelvesOverview'),
                    scope: 'store',
                    translationKey: 'navigation.shelvesOverview',
                },
            ],
        },

        {
            path: '/brandEditor',
            link: '/brandEditor',
            component: () => import('./brandEditor/brandEditor'),
            icon: faEyedropper,
            translationKey: 'brandEditor.title',
            group: 'navigation.administration',
            claims: ['branding/write', 'branding/read'],
            children: [
                {
                    path: 'create',
                    component: () => import('./brandEditor/brandEditorEdit'),
                    nested: true,
                },
                {
                    path: 'edit/:id',
                    component: () => import('./brandEditor/brandEditorEdit'),
                    nested: true,
                },
            ],
        },

        {
            path: '/barcodeClassification',
            component: () => import('./barcodeClassification/bcTable'),
            icon: faBarcode,
            translationKey: 'navigation.barcodeClassification',
            group: 'navigation.administration',
            claims: ['barcodeClassifications/read'],
            children: [
                {
                    path: 'test/:code?',
                    component: () => import('./barcodeClassification/bcTester'),
                    nested: true,
                },
                {
                    path: 'new/:template?',
                    component: () => import('./barcodeClassification/bcForm'),
                    nested: true,
                    claims: ['barcodeClassifications/write'],
                },
                {
                    path: ':id',
                    component: () => import('./barcodeClassification/bcForm'),
                    nested: true,
                    claims: ['barcodeClassifications/write'],
                },
            ],
        },

        {
            path: '/support',
            icon: faNotebook,
            translationKey: 'supportInfo.title',
            group: 'navigation.administration',
            claims: ['supportInfo/read'],
            redirect: 'info',
            children: [
                {
                    path: 'info',
                    component: () => import('./supportInfo'),
                    translationKey: 'supportInfo.support.title',
                    group: 'navigation.administration',
                    claims: ['supportInfo/read'],
                    scope: 'storeDraft',
                    children: [
                        {
                            path: 'sections',
                            component: () => mapImport(import('./supportInfo/supportInfoSections'), 'SupportInfoSectionsDialog'),
                            nested: true,
                        },
                    ],
                },
                {
                    path: 'events',
                    component: () => import('./events'),
                    group: 'navigation.administration',
                    roles: ['admin', 'pentland', 'itOperations'],
                    translationKey: 'supportInfo.events.title',
                },
            ],
        },

        //Account
        {
            path: '/me',
            component: () => import('./user/profile.component'),
        },
    ),
];

export default routes;

async function loadComponent(component?: () => Promise<{ default: ComponentType | ComponentType<{ match: match }> }>) {
    if (!component) {
        return { default: NotImplemented };
    }

    try {
        return await component();
    } catch (e: any) {
        console.error('Failed to load module:', e, e.response);

        if (!import.meta.env.PROD) {
            return {
                default: () => (
                    <Box sx={{ p: 1 }}>
                        <RenderError error={e} />{' '}
                    </Box>
                ),
            };
        }

        console.warn('Out of date chunks. Reloading the page...');
        window.location.reload();
        return { default: () => null };
    }
}

export function useAccessibleRoutes() {
    const { isLoggedIn, hasClaim, getActiveRoles, isAuthLoading } = useAuth();
    const { storeGuid } = useStoreGuid();
    const stores = useAppSelector((state) => state.stores.stores);
    const [config, { error: configError }] = storeService.getConfig.useAction(storeGuid);

    const filter = useCallback(
        (routes: Route[]): Route[] =>
            routes.flatMap((route) => {
                // If route is a store route => don't show without storeGuid set
                if (route.scope === 'store' && !storeGuid) {
                    return [];
                }

                // If route is a global route => only show without storeGuid set
                if (route.scope === 'global' && storeGuid) {
                    return [];
                }

                if (route.scope === 'store' && !stores[storeGuid ?? '']?.storeDbName) {
                    // If route is a store route and not draft => don't show if store has no db setup yet
                    return [];
                }

                if (route.scope === 'storeDraft' && !storeGuid) {
                    return [];
                }

                const routeClaims: [string, any?, any?][] | undefined = (
                    Array.isArray(route.claims) ? route.claims : storeGuid ? route.claims?.store : route.claims?.global
                )?.map((claim) => (Array.isArray(claim) ? claim : [claim, storeGuid ? 'store' : 'global']));

                if (routeClaims && !routeClaims.every((claim) => hasClaim(...claim))) {
                    return [];
                }

                if (route.roles) {
                    const activeRoles = getActiveRoles(storeGuid ? 'store' : 'global');

                    if (!activeRoles.has('admin') && !route.roles.some((role) => activeRoles.has(role))) {
                        return [];
                    }
                }

                const filteredChildren = route.children && filter(route.children);

                if (route.showOnlyWithChildren && !filteredChildren?.length) {
                    return [];
                }

                // Hide conditionally-shown routes until config is loaded to prevent "pop-outs" after login
                if (route.show && (!config || !route.show(config))) {
                    return [];
                }

                return [
                    {
                        ...route,
                        children: filteredChildren,
                    },
                ];
            }),
        [storeGuid, stores, hasClaim, getActiveRoles, config],
    );

    const accessibleRoutes = useMemo(() => (isLoggedIn ? filter(routes) : []), [isLoggedIn, filter]);
    const isConditionalRoutesLoading = !config && !configError;

    return { isLoggedIn, accessibleRoutes, isLoading: isAuthLoading, isConditionalRoutesLoading };
}

export function useVisibleRoutes() {
    const { accessibleRoutes } = useAccessibleRoutes();
    const t = useTranslator();
    const enT = useTranslator('en');

    const translate = useCallback(
        (route: Route): Route => ({
            ...route,
            label: route.translationKey ? (t.unknown(route.translationKey) as string) : route.label,
            englishLabel: route.translationKey ? (enT.unknown(route.translationKey) as string) : undefined,
            children: route.children?.map(translate),
        }),
        [t, enT],
    );

    return useMemo(
        () => accessibleRoutes.filter((route) => route.translationKey && !route.parentInNavigation).map(translate),
        [accessibleRoutes, translate],
    );
}

Object.assign(window, { routes });
